import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { blogAPI } from '../services/api';
import { safeJsonLd } from '../utils/seo';
import '../styles/pages/blog.css';

const BlogIndexPage = () => {
  const [params, setParams] = useSearchParams();
  const page = Number(params.get('page') || 1);
  const q    = params.get('q') || '';

  const [data, setData] = useState({ posts: [], pagination: { totalPages: 1, total: 0 } });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [postsRes, catsRes] = await Promise.all([
          blogAPI.listPosts({ page, limit: 12, q: q || undefined }),
          blogAPI.listCategories(),
        ]);
        setData({ posts: postsRes.data.data, pagination: postsRes.data.pagination });
        setCategories(catsRes.data.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [page, q]);

  const goPage = (p) => {
    const next = new URLSearchParams(params);
    next.set('page', p);
    setParams(next);
  };

  const blogJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Blog LifeUp',
    url: 'https://lifyup.com.br/blog',
    description: 'Artigos sobre recuperação, autocontrole e bem-estar digital.',
  };

  return (
    <div className="lp-theme blog-page">
      <Helmet>
        <title>Blog — LifeUp</title>
        <meta name="description" content="Artigos sobre recuperação, autocontrole e bem-estar digital." />
        <link rel="canonical" href="https://lifyup.com.br/blog" />
        <meta property="og:title" content="Blog — LifeUp" />
        <meta property="og:description" content="Artigos sobre recuperação, autocontrole e bem-estar digital." />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{safeJsonLd(blogJsonLd)}</script>
      </Helmet>

      <Header />
      <main className="blog-container">
        <div className="blog-hero">
          <h1>Blog LifeUp</h1>
          <p>Artigos, guias e histórias para sua jornada de recuperação.</p>
        </div>

        {categories.length > 0 && (
          <div className="blog-filters">
            <Link to="/blog" className={`blog-filter-chip ${!params.get('cat') ? 'active' : ''}`}>Todas</Link>
            {categories.map((c) => (
              <Link key={c.id} to={`/blog/categoria/${c.slug}`} className="blog-filter-chip">{c.name}</Link>
            ))}
          </div>
        )}

        {loading ? (
          <div className="blog-empty">Carregando…</div>
        ) : data.posts.length === 0 ? (
          <div className="blog-empty">Nenhum post publicado ainda.</div>
        ) : (
          <div className="blog-grid">
            {data.posts.map((p) => (
              <Link key={p.id} to={`/blog/${p.slug}`} className="blog-card">
                {p.cover_image && <img src={p.cover_image} alt={p.title} className="blog-card__img" />}
                <div className="blog-card__body">
                  <div className="blog-card__meta">
                    {p.categories?.name && <span>{p.categories.name}</span>}
                    {p.published_at && <span>· {new Date(p.published_at).toLocaleDateString('pt-BR')}</span>}
                    {p.reading_time && <span>· {p.reading_time} min</span>}
                  </div>
                  <h2 className="blog-card__title">{p.title}</h2>
                  {p.excerpt && <p className="blog-card__excerpt">{p.excerpt}</p>}
                </div>
              </Link>
            ))}
          </div>
        )}

        {data.pagination.totalPages > 1 && (
          <div className="blog-pagination">
            <button disabled={page <= 1} onClick={() => goPage(page - 1)}>Anterior</button>
            {Array.from({ length: data.pagination.totalPages }, (_, i) => i + 1).map((p) => (
              <button key={p} className={p === page ? 'active' : ''} onClick={() => goPage(p)}>{p}</button>
            ))}
            <button disabled={page >= data.pagination.totalPages} onClick={() => goPage(page + 1)}>Próxima</button>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default BlogIndexPage;
